import {
  DEFAULT_SOLVER_SETTINGS,
  getState,
  nearestPolytopeVertex,
  type SolverMode,
  type SolverSettings,
  type State,
} from "@/features/core/store";
import type { ShareSettings } from "@/features/share/sharedState";
import type { ResultRenderPayload } from "@/features/solver/solverService";
import type { SolverWorkerPayload } from "@/features/solver/solverWorker";
import { hasPolytopeLines } from "@lpviz/polytope/polytopeTypes";
import { isEnteringRule, isLeavingRule } from "@lpviz/solver-engine/simplex";
import { interiorPoint3 } from "@lpviz/polytope/polytope3";

export type SolverSettingUpdater = <K extends keyof SolverSettings>(key: K, value: SolverSettings[K]) => void;

export type SolverControl = {
  mode: SolverMode;
  isSelectable: (state: State) => boolean;
  getRunBlock: (state: State) => ResultRenderPayload | null;
  collectShareSettings: () => ShareSettings;
  applySharedSettings: (settings: ShareSettings) => void;
  buildRequest: (state: State) => SolverWorkerPayload | null;
};

// Every share key is also a solver-settings key.
type SharedKey = keyof ShareSettings & keyof SolverSettings;

// The share payload is untrusted (see sharedState.ts). A value reaches the
// store only when it has the default's shape: a finite number, a boolean, or
// one of the engine's rule names. Anything else is dropped so a hand-edited
// link can neither blank a <select> nor throw inside the settings panel's
// store subscriber.
function isValidSharedSetting<K extends SharedKey>(
  key: K,
  value: unknown,
): value is SolverSettings[K] {
  if (key === "simplexEnteringRule") return isEnteringRule(value);
  if (key === "simplexLeavingRule") return isLeavingRule(value);
  const fallback: unknown = DEFAULT_SOLVER_SETTINGS[key];
  return typeof fallback === "number"
    ? Number.isFinite(value)
    : typeof value === typeof fallback;
}

const hasFeasibleRegion = (state: State): boolean =>
  state.problemMode === "3d"
    ? state.polytope3?.kind === "bounded"
    : hasPolytopeLines(state.polytope) &&
      (state.polytope.kind === "bounded" || state.polytope.kind === "unbounded");

const isEmptyRegion = (state: State): boolean => (state.problemMode === "3d" ? state.polytope3 !== null && state.polytope3.kind !== "bounded" : hasPolytopeLines(state.polytope) && state.polytope.kind === "empty");

// the objective vector + constraint lines guard common to every buildRequest
function objectiveBase(state: State) {
  if (state.problemMode === "3d") {
    if (!state.objectiveVector3 || !state.polytope3 || state.polytope3.kind !== "bounded") {
      return null;
    }
    return {
      lines: state.polytope3.planes,
      objective: Float64Array.of(state.objectiveVector3.x, state.objectiveVector3.y, state.objectiveVector3.z),
    };
  }
  if (!state.objectiveVector || !hasPolytopeLines(state.polytope)) return null;
  return {
    lines: state.polytope.lines,
    objective: Float64Array.of(state.objectiveVector.x, state.objectiveVector.y),
  };
}

// The dragged start point as a solver payload, or absent when never set (the
// solvers then keep their exact legacy initialization).
function startPointPayload(state: State): { startPoint?: number[] } {
  const point = state.solverStartPoint;
  if (!point) return {};
  return {
    startPoint:
      state.problemMode === "3d"
        ? [point.x, point.y, point.z ?? 0]
        : [point.x, point.y],
  };
}

const messageBlocks = (
  header: string,
  message: string,
): ResultRenderPayload => ({
  type: "blocks",
  blocks: [
    { className: "iterate-header", text: header },
    { className: "iterate-item-nohover", text: message },
  ],
});

export function createSolverControls({ updateSolverSetting, hasUnboundedObjectiveDirection }: { updateSolverSetting: SolverSettingUpdater; hasUnboundedObjectiveDirection: (state: State) => boolean }): SolverControl[] {
  const collectShared = (keys: readonly SharedKey[]): ShareSettings => {
    const s = getState().solverSettings;
    const out: ShareSettings = {};
    for (const k of keys) (out[k] as SolverSettings[SharedKey]) = s[k];
    return out;
  };
  const applyShared = (settings: ShareSettings, keys: readonly SharedKey[]): void => {
    for (const k of keys) {
      const v: unknown = settings[k];
      if (isValidSharedSetting(k, v)) updateSolverSetting(k, v);
    }
  };

  return [
    {
      mode: "central",
      isSelectable: (s) => hasFeasibleRegion(s) && !hasUnboundedObjectiveDirection(s),
      getRunBlock: (s) => {
        if (!hasPolytopeLines(s.polytope)) return null;
        if (s.polytope.kind === "empty") return messageBlocks("No valid region", "Central Path requires a feasible region.");
        if (hasUnboundedObjectiveDirection(s)) return messageBlocks("Solver unavailable", "Central Path is disabled when the objective points in an unbounded direction. Select IPM, PDHG, or Simplex to see how they handle this unbounded problem.");
        return null;
      },
      collectShareSettings: () => collectShared(["centralPathIter"]),
      applySharedSettings: (settings) => applyShared(settings, ["centralPathIter"]),
      buildRequest: (s) => {
        const base = objectiveBase(s);
        if (!base) return null;
        if (s.problemMode === "3d") {
          const interior = s.polytope3 ? interiorPoint3(s.polytope3.planes, s.polytope3.vertices) : null;
          if (!interior) return null;
          return {
            solver: "central",
            vertices: [],
            ...base,
            interiorPoint: [interior.x, interior.y, interior.z],
            niter: Math.max(1, s.solverSettings.centralPathIter || 1),
          };
        }
        if (!hasPolytopeLines(s.polytope)) return null;
        return {
          solver: "central",
          vertices: s.polytope.vertices,
          ...base,
          niter: Math.max(1, s.solverSettings.centralPathIter || 1),
        };
      },
    },
    {
      mode: "ipm",
      isSelectable: hasFeasibleRegion,
      getRunBlock: (s) => (isEmptyRegion(s) ? messageBlocks("No valid region", "IPM requires a feasible region.") : null),
      collectShareSettings: () => collectShared(["alphaMax", "correctorThreshold", "maxitIPM"]),
      applySharedSettings: (settings) => applyShared(settings, ["alphaMax", "correctorThreshold", "maxitIPM"]),
      buildRequest: (s) => {
        const base = objectiveBase(s);
        if (!base) return null;
        const ss = s.solverSettings;
        return {
          solver: "ipm",
          ...base,
          ...startPointPayload(s),
          alphaMax: ss.alphaMax,
          correctorThreshold: ss.correctorThreshold,
          maxit: Math.max(1, ss.maxitIPM || 1),
        };
      },
    },
    {
      mode: "simplex",
      isSelectable: hasFeasibleRegion,
      getRunBlock: (s) =>
        isEmptyRegion(s)
          ? messageBlocks(
              "No valid region",
              "Simplex requires a valid feasible region.",
            )
          : null,
      collectShareSettings: () =>
        collectShared(["simplexDualMode", "simplexEnteringRule", "simplexLeavingRule"]),
      applySharedSettings: (settings) =>
        applyShared(settings, ["simplexDualMode", "simplexEnteringRule", "simplexLeavingRule"]),
      buildRequest: (s) => {
        const base = objectiveBase(s);
        if (!base) return null;
        // Simplex consumes the start as a vertex (the marker snaps to one);
        // dual mode has no safe start-point interpretation and ignores it.
        const snapped =
          !s.solverSettings.simplexDualMode && s.solverStartPoint
            ? nearestPolytopeVertex(s, s.solverStartPoint)
            : null;
        return {
          solver: "simplex",
          ...base,
          ...(snapped
            ? {
                startVertex:
                  s.problemMode === "3d"
                    ? [snapped.x, snapped.y, snapped.z ?? 0]
                    : [snapped.x, snapped.y],
              }
            : {}),
          dual: s.solverSettings.simplexDualMode,
          enteringRule: s.solverSettings.simplexEnteringRule,
          leavingRule: s.solverSettings.simplexLeavingRule,
        };
      },
    },
    {
      mode: "ellipsoid",
      isSelectable: hasFeasibleRegion,
      getRunBlock: (s) =>
        isEmptyRegion(s)
          ? messageBlocks(
              "No valid region",
              "The ellipsoid method requires a feasible region.",
            )
          : null,
      collectShareSettings: () =>
        collectShared([
          "maxitEllipsoid",
          "ellipsoidDeepCuts",
          "ellipsoidRayShoot",
          "ellipsoidQueryPoint",
          "ellipsoidInitialScale",
        ]),
      applySharedSettings: (settings) =>
        applyShared(settings, [
          "maxitEllipsoid",
          "ellipsoidDeepCuts",
          "ellipsoidRayShoot",
          "ellipsoidQueryPoint",
          "ellipsoidInitialScale",
        ]),
      buildRequest: (s) => {
        const base = objectiveBase(s);
        if (!base) return null;
        // the drawn region's extreme points bound the initial ellipsoid; the
        // 3-variable solid supplies its hull corners
        const vertices =
          s.problemMode === "3d"
            ? s.polytope3!.vertices.map((v) => [v.x, v.y, v.z])
            : hasPolytopeLines(s.polytope)
              ? s.polytope.vertices
              : null;
        if (!vertices) return null;
        const ss = s.solverSettings;
        return {
          solver: "ellipsoid",
          vertices,
          ...base,
          maxit: Math.max(1, ss.maxitEllipsoid || 1),
          deepCuts: ss.ellipsoidDeepCuts,
          rayShoot: ss.ellipsoidRayShoot,
          queryPoint: ss.ellipsoidQueryPoint,
          initialScale: ss.ellipsoidInitialScale,
        };
      },
    },
    {
      mode: "pdhg",
      isSelectable: hasFeasibleRegion,
      getRunBlock: () => null,
      collectShareSettings: () => collectShared(["pdhgEta", "pdhgTau", "maxitPDHG", "pdhgIneqMode", "pdhgHalpernMode", "pdhgColorByBasis"]),
      applySharedSettings: (settings) => applyShared(settings, ["pdhgEta", "pdhgTau", "maxitPDHG", "pdhgIneqMode", "pdhgHalpernMode", "pdhgColorByBasis"]),
      buildRequest: (s) => {
        const base = objectiveBase(s);
        if (!base) return null;
        const ss = s.solverSettings;
        return {
          solver: "pdhg",
          ...base,
          ...startPointPayload(s),
          ineq: ss.pdhgIneqMode,
          halpern: ss.pdhgHalpernMode,
          maxit: Math.max(1, ss.maxitPDHG || 1),
          eta: ss.pdhgEta,
          tau: ss.pdhgTau,
          colorByBasis: ss.pdhgColorByBasis,
        };
      },
    },
  ];
}
